/**
 * Shell state — top-level UI routing and sidebar collapse.
 *
 * The shell owns only view-routing concerns:
 *   - which channel is active
 *   - which DM conversation (if any) is open in the sidebar
 *   - whether the DM sidebar is collapsed
 *   - unread-counts per channel (populated from server events in later
 *     deliverables — empty in P1.7 D4)
 *
 * Everything world-mechanical lives in the `world` module; wire/transport
 * lives in `wire`. The shell slice never touches either directly.
 */

import { create } from 'zustand';

import { CLIENT_CHANNELS, DEFAULT_CHANNEL_ID } from '../config/channels';

export interface ShellState {
  /** Active channel id — `CLIENT_CHANNELS[i].id`. */
  activeChannel: string;
  /** Open DM conversation id, or null. */
  activeDm: string | null;
  /** DM sidebar collapse state. Default: collapsed in world view per §13.1. */
  dmSidebarCollapsed: boolean;
  /** Unread counts per channel (populated by later deliverables). */
  unread: Record<string, number>;

  // ─── Actions ─────────────────────────────────────────────────────────
  selectChannel: (id: string) => void;
  selectDm: (id: string | null) => void;
  toggleDmSidebar: () => void;
  setUnread: (channelId: string, count: number) => void;
  clearUnread: (channelId: string) => void;
}

/** Validate a channel id at boundary. Unknown ids fall back to default. */
function normalizeChannel(id: string): string {
  return CLIENT_CHANNELS.some((c) => c.id === id) ? id : DEFAULT_CHANNEL_ID;
}

export const useShell = create<ShellState>((set) => ({
  activeChannel: DEFAULT_CHANNEL_ID,
  activeDm: null,
  dmSidebarCollapsed: true,
  unread: {},

  selectChannel: (id) =>
    set(() => ({
      activeChannel: normalizeChannel(id),
    })),
  selectDm: (id) => set(() => ({ activeDm: id })),
  toggleDmSidebar: () =>
    set((s) => ({ dmSidebarCollapsed: !s.dmSidebarCollapsed })),
  setUnread: (channelId, count) =>
    set((s) => ({ unread: { ...s.unread, [channelId]: count } })),
  clearUnread: (channelId) =>
    set((s) => {
      const next = { ...s.unread };
      delete next[channelId];
      return { unread: next };
    }),
}));
